/**
 * Fork guidance - "which way do I go here?" at every branch in the road.
 *
 * significance.ts deliberately swallows most forks, and it is right to: a run
 * of four "keep right to stay on 304" announcements over 8 km was noise, and it
 * aimed the distance countdown at a non-event. But swallowing them for the
 * VOICE also swallowed them for the EYES, and that leaves the driver at a
 * physical split in the carriageway with a card counting down to a turn 8 km
 * away and nothing at all about the choice in front of the windscreen.
 *
 * So the channels are separated. Every fork the route passes through gets a
 * strip on the maneuver card for as long as it is within range - permanently
 * visible, never interrupting - while the voice says one short cue close in and
 * the 1.5 km / 300 m ladder stays reserved for maneuvers that really are turns.
 *
 * A fork that IS the significant maneuver is not reported here: the main card
 * is already showing it, and two rows describing the same junction is worse
 * than one.
 */

import type { RouteStep } from '../core/types';
import type { IndexedStep } from './routeIndex';

export interface ForkInfo {
  /** Which side of the split to hold. */
  side: 'left' | 'right';
  /** Metres from the vehicle to the split. */
  distance: number;
  /** Index into RouteIndex.steps, so callers can key "already spoken" state. */
  stepIndex: number;
  /** The branch we take. */
  step: RouteStep;
  /** True when this fork merely stays on the road we are already driving. */
  staysOnRoad: boolean;
}

/**
 * How far ahead a fork is worth showing, in metres.
 *
 * Wider than the lane range: a motorway split has to be read while there is
 * still time to drift across, and unlike a lane bar a fork strip costs nothing
 * to leave on screen. At 100 km/h this is ~1.7 km, in town it floors at 800 m.
 */
export function forkVisibilityRange(speedMs: number): number {
  return Math.min(3000, Math.max(800, speedMs * 60));
}

/** Metres out at which the single spoken cue fires. */
export const FORK_SPEAK_AT_M = 400;

/**
 * A maneuver that picks a branch of the road rather than turning across it.
 *
 * Ramps count. An `off ramp` the route folds away is the same decision as a
 * fork from the driver's seat - the carriageway divides and one side is wrong -
 * and it was an unannounced keep-left under a flyover that started all of this.
 */
function isFork(step: RouteStep): boolean {
  const type = (step.maneuver.type ?? '').toLowerCase();
  if (type === 'depart' || type === 'arrive') return false;
  const mod = (step.maneuver.modifier ?? '').toLowerCase();
  if (type === 'fork' || type === 'off ramp' || type === 'on ramp') {
    return mod.includes('left') || mod.includes('right');
  }
  return mod === 'slight left' || mod === 'slight right';
}

/**
 * The next fork between the vehicle and the maneuver being counted down to.
 *
 * `targetIdx` is the next SIGNIFICANT step. Only forks strictly before it are
 * returned - one at index `targetIdx` is the main card's own maneuver.
 */
export function nextFork(
  steps: IndexedStep[],
  fromStepIdx: number,
  targetIdx: number,
  along: number,
  speedMs: number,
): ForkInfo | null {
  const range = forkVisibilityRange(speedMs);

  for (let i = Math.max(0, fromStepIdx); i < Math.min(targetIdx, steps.length); i++) {
    const s = steps[i];
    if (i <= fromStepIdx && s.startDistance <= along) continue; // already driven through
    if (!isFork(s.step)) continue;

    const distance = s.startDistance - along;
    if (distance < 0) continue;
    if (distance > range) return null; // nothing near enough yet

    const mod = (s.step.maneuver.modifier ?? '').toLowerCase();
    const prev = steps[i - 1]?.step ?? null;
    return {
      side: mod.includes('left') ? 'left' : 'right',
      distance,
      stepIndex: i,
      step: s.step,
      staysOnRoad: sameRoad(prev, s.step),
    };
  }
  return null;
}

/** Do these two steps carry the same road number, or failing that the same name? */
function sameRoad(a: RouteStep | null, b: RouteStep | null): boolean {
  if (!a || !b) return false;
  const refA = (a.ref ?? '').trim();
  const refB = (b.ref ?? '').trim();
  if (refA && refB) return refA === refB;
  const nameA = (a.name ?? '').trim();
  const nameB = (b.name ?? '').trim();
  return !!nameA && nameA === nameB;
}
